import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppConstants } from '@core/entities/app-constants';
import { LocalService } from '@app/@core/services/local.service';
import { IDynamicPageValue, IMenuItem, PageEntry } from './page-type';


const PAGES_PROFILE = '/pages/profile';
const ROUTE_URL = '/pages/main-notification';
const PERCENTAGE_MULTIPLIER = 100;
const SECONDS_PER_MINUTE = 60;
const PADDING_WIDTH = 2;
const SCALE_THRESHOLD = 175;
const SCALE_TOLERANCE = 1;
const EXPONENT_BASE = 2;
const RANDOM_EXPONENT = 32;
const HEXADECIMAL_BASE = 16;
const GUID_BIT_MASK_1 = 0x3;
const GUID_BIT_MASK_2 = 0x8;
const BREAKPOINT_WIDTH = 961;

/**
 * Helper service for PagesComponent providing utility functions and helper methods.
 *
 * @export
 * @class PagesHelperService
 */
@Injectable({
  providedIn: 'root'
})
export class PagesHelperService {
  constructor(private readonly _router: Router) {}

  /**
   * Updates the countdown timer display.
   *
   * @param {number} seconds - The total remaining seconds.
   * @return {*}  {string} Formatted countdown string (MM:SS).
   */
  static updateCountdown(seconds: number): string {
    const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
    const remainingSeconds = seconds % SECONDS_PER_MINUTE;
    return `${minutes.toString().padStart(PADDING_WIDTH, '0')}:${remainingSeconds.toString().padStart(PADDING_WIDTH, '0')}`;
  }

  /**
   * Checks the current route and determines if show count should be true.
   *
   * @return {*}  {boolean} True if on notification route.
   */
  conditionCheckShowCount(): boolean {
    return this._router.url === ROUTE_URL;
  }

  /**
   * Checks the device scale and removes the 'hmenu' class if conditions are met.
   *
   */
  
  static checkScaleAndRemoveClass(): void {
    const scale = globalThis.devicePixelRatio * PERCENTAGE_MULTIPLIER;
    const element = document.querySelector('.pageWrapper.active');

    if (Math.abs(scale - SCALE_THRESHOLD) < SCALE_TOLERANCE && element?.classList.contains('hmenu')) {
      element.classList.remove('hmenu');
    }
  }

  /**
   * Generates a cryptographically secure random number between 0 and 1.
   *
   * @return {*} 
   */
  static generateSecureRandomNumber(): number {
    return globalThis.crypto.getRandomValues(new Uint32Array(1))[0] as number / EXPONENT_BASE ** RANDOM_EXPONENT;
  }

  /**
   * Generates a unique GUID with a custom prefix (`card-inal-`).
   * Uses a combination of random numbers to ensure uniqueness.
   * @return {*}  {string} A unique GUID string.
   */
  static generateGuid(): string {
    return (
      'card-inal-' +
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.floor((AppConstants.generateNumber() * HEXADECIMAL_BASE) / AppConstants.generateNumber());
        const v = c === 'x' ? r : (r & GUID_BIT_MASK_1) | GUID_BIT_MASK_2;
        return v.toString(HEXADECIMAL_BASE);
      })
    );
  }

  /**
   * Checks if the given value is a string.
   *
   * @param {*} value - The value to check.
   * @return {*}  {boolean} True if the value is a string, otherwise false. 
   */
  static isString(value: unknown): value is string {
    return typeof value === 'string';
  }

  /**
   * Retrieves the last segment of the current URL path.
   *
   * @return {*}  The last segment of the URL.
   */
  static getCurrentUrlPath(): string {
    return globalThis.location.pathname.split('/').pop() as string;
  }

  /**
   * Gets the page name from the page object, with fallback to the object itself.
   *
   * @param {*} page - The page object.
   * @return {*}  {string} The page name or the page object.
   */
  private static getPageName(page: PageEntry): string {
    if (!page) return '';
    if (typeof page === 'string') return page;
    return page.name ?? page.pagename ?? page.page ?? '';
  }

  private static isDynamicPageValue(page: PageEntry): page is IDynamicPageValue {
    return !!page && typeof page === 'object';
  }

  /**
   * Handles dynamic pages by determining the appropriate page name based on the current URL path.  
   *
   * @param {*} getCurrentPage - The current dynamic page object.
   * @param {*} currentUrlPath - The last segment of the current URL.
   * @return {*}  {string} The determined page name.
   */
  static handleDynamicPages(getCurrentPage: PageEntry, currentUrlPath: string): string {
    if (!PagesHelperService.isDynamicPageValue(getCurrentPage) || !currentUrlPath) {
      return PagesHelperService.getPageName(getCurrentPage);
    }

    if (getCurrentPage.route === `/${currentUrlPath}`) {
      return PagesHelperService.getPageName(getCurrentPage);
    }

    if (getCurrentPage.submenu?.length) {
      const activeSubmenuPage = getCurrentPage.submenu.find(item => item?.active);
      return PagesHelperService.getPageName(activeSubmenuPage ?? getCurrentPage);
    }

    return PagesHelperService.getPageName(getCurrentPage);
  }

  /**
   * Checks if the route matches the current page route.
   *
   * @param {*} getCurrentPage - The current page object.
   * @param {string} currentUrlPath - The last segment of the current URL.
   * @return {*}  {boolean} True if route matches.
   */
  private static isRouteMatch(getCurrentPage: IDynamicPageValue | undefined, currentUrlPath: string): boolean {
    return !!getCurrentPage && getCurrentPage.route === `/${currentUrlPath}`;
  }

  /**
   * Handles setting the page name for standard pages based on the current route.
   *
   * @param {*} getCurrentPage - The current page object.
   * @param {*} currentUrlPath - The last segment of the current URL.
   * @param {string} routerUrl - The current router URL.
   * @return {*}  {string} The determined page name or null if should return early.
   */
  static handleCurrentPage(getCurrentPage: PageEntry, currentUrlPath: string, routerUrl: string): string | null {
    if (routerUrl === PAGES_PROFILE) return 'Profile';
    if (PagesHelperService.isDynamicPageValue(getCurrentPage)) {
      if (PagesHelperService.isRouteMatch(getCurrentPage, currentUrlPath)) return PagesHelperService.getPageName(getCurrentPage);
      if (getCurrentPage.submenu?.length) return PagesHelperService.handleSubmenuPages(getCurrentPage);
      return PagesHelperService.getPageName(getCurrentPage);
    }
    return PagesHelperService.getPageName(getCurrentPage);
  }

  /**
   * Handles setting the current page name based on active and matching submenu pages.
   *
   * @param {*} getCurrentPage - The current page object containing submenu information.
   * @return {*}  {string} The determined page name.
   */
  static handleSubmenuPages(getCurrentPage: IDynamicPageValue): string {
    const activeSubmenuPage = getCurrentPage.submenu?.find(item => item?.active);
    const matchingSubmenuPage = getCurrentPage.submenu?.find(item =>
      globalThis.location.pathname.includes(item?.route || item?.id as string)
    );

    let pagename = PagesHelperService.getPageName(activeSubmenuPage ?? getCurrentPage);
    if (matchingSubmenuPage) {
      pagename = PagesHelperService.getPageName(matchingSubmenuPage);
    }
    return pagename;
  }
  /**
   * Stores the current page name in sessionStorage and returns it as the previously selected menu.
   * 
   * @param {string} pagename - The page name to store.
   * @param {LocalService} sessionStorage - The sessionStorage service instance.
   * @return {*}  {string} The page name.
   */
  static setPagename(pagename: string, sessionStorage?: LocalService): string {
    sessionStorage?.setItem('pagename', pagename);
    return pagename;
  }

  /**
   * Checks if the pagename is invalid or not set, and restores it from sessionStorage if necessary.
   *
   * @param {*} pagename - The current pagename value.
   * @param {LocalService} sessionStorage - The sessionStorage service instance.
   * @return {*}  {string} The valid pagename.
   */
  static checkAndRestorePagename(pagename: string | null | undefined, sessionStorage?: LocalService): string {
    if (!pagename || typeof pagename === 'object') {
      return sessionStorage?.getItem('pagename') ?? '';
    }
    return pagename;
  }

  /**
   * Retrieves the current menu name based on the router URL or active user menu.
   *
   * @param {Array} currentUser - Array of current user menu items.
   * @return {*}  {*}
   */
  getCurrentMenu(currentUser: IMenuItem[]): string {
    if (this._router.url === PAGES_PROFILE) {
      return 'Profile';
    }
    if (this._router.url === ROUTE_URL) {
      return 'Notifications';
    }
    if (currentUser.length > 0) {
      const activeItem = currentUser.find(item => item?.active);
      return activeItem?.name ?? '';
    }
    return '';
  }

  /**
   * Listens for window resize events and logs screen width changes.
   *
   */
  static windowResize():void {
    globalThis.addEventListener('resize', _e => {
      const windowWidth = globalThis.innerWidth;
      if (windowWidth > BREAKPOINT_WIDTH) {
        console.log('from 768px');
      } else {
        console.log('less 768px');
      }
    });
  }

  /**
   * Shows or hides the dynamic ribbon element;;; based on the given page name.
   *
   * @param {string} pagename - The current page name to check.
   */
  static handleDynamicRibbonDisplay(pagename: string): void {
    const ribbon = document.querySelector('app-dynamic-ribbon-page');
    if (!(ribbon instanceof HTMLElement)) return;

    if (pagename === 'Attendance Tracking' || pagename === 'Attendance') {
      ribbon.style.display = 'none';
    } else {
      ribbon.style.display = '';
    }
  }

  static resolvePageMetadata(value: IDynamicPageValue | string | undefined): { name: string; icon: string } {
    if (value && typeof value === 'object') {
      return {
        name: value.name ?? value.pagename ?? '',
        icon: value.additionalinfo?.icon ?? ''
      };
    }
    if (typeof value === 'string') {
      return { name: value, icon: '' };
    }
    return { name: '', icon: '' };
  }

}
